<template>
    <div
        class="fixed top-16 hidden h-[calc(100vh-4rem)] shrink-0 flex-col overflow-y-hidden bg-white pt-5 md:flex transition-[width] duration-200"
        :class="{
            'z-30': opened,
            'border-r border-gray-200': !opened,
            'w-16': !opened,
            'w-[250px]': opened,
            'xl:w-[250px]': true,
        }"
    >
        <transition
            enter-active-class="transition duration-200 ease-out transform"
            enter-from-class="-translate-x-10 opacity-0 "
            enter-to-class="translate-x-0 opacity-100 "
            leave-active-class="transition duration-100 ease-in transform"
            leave-from-class="translate-x-0 opacity-100"
            leave-to-class="-translate-x-10 opacity-0"
        >
            <div
                v-if="opened"
                class="hidden xl:hidden"
                :class="{
                    'absolute left-[63px] top-0 z-[-2] h-full w-[161px] bg-white md:block':
                        opened,
                }"
            ></div>
        </transition>

        <div
            class="relative z-20 flex h-full flex-col pt-[24px] overflow-y-hidden"
        >
            <nav
                class="flex-1 space-y-[5px] px-[10px]"
                aria-label="Sidebar"
                role="navigation"
            >
                <div class="relative hidden h-10 md:block xl:hidden">
                    <transition
                        enter-active-class="transition ease-in-out duration-800"
                        enter-from-class="transform scale-0 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-100 ease-in "
                        leave-from-class="opacity-100 "
                        leave-to-class="opacity-0 "
                    >
                        <button
                            v-if="!opened"
                            class="absolute mx-2 mb-[16px] h-6 w-6 text-gray-600 xl:hidden"
                            @click="$emit('open')"
                        >
                            <Bars3BottomLeftIcon />
                        </button>
                    </transition>

                    <transition
                        enter-active-class="transition duration-1000 ease-in-out"
                        enter-from-class="transform rotate-180 opacity-0 scale-80"
                        enter-to-class="transform scale-100 rotate-0 opacity-100"
                        leave-active-class="transition duration-75 ease-in "
                        leave-from-class="opacity-100 "
                        leave-to-class="opacity-0 "
                    >
                        <button
                            v-if="opened"
                            class="absolute mx-2 mb-[16px] h-6 w-6 text-gray-600 xl:hidden"
                            @click="$emit('close')"
                        >
                            <XMarkIcon />
                        </button>
                    </transition>
                </div>

                <inertia-link
                    v-for="link in links"
                    :key="link.href"
                    v-tooltip="{
                        content: link.name,
                        delay: { show: 1250, hide: 150 },
                    }"
                    :href="link.href"
                    :aria-current="page.startsWith(link.id) ? 'page' : null"
                    class="group block w-full"
                    @click="$emit('close')"
                >
                    <div
                        class="flex h-12 w-full items-center rounded-[6.75px] px-4 py-3 transition-all duration-200"
                        :class="[
                            (opened ? 'gap-7' : 'justify-center') +
                                ' xl:justify-start xl:gap-7',
                            page.startsWith(link.id)
                                ? 'bg-gray-200 text-gray-600'
                                : 'text-gray-500 hover:bg-gray-200 hover:text-gray-600',
                        ]"
                        :data-helper-tooltip="link.name.toLocaleLowerCase()"
                    >
                        <component
                            :is="link.icon"
                            class="h-5 w-5 shrink-0 transition duration-200 group-hover:text-gray-600"
                        />

                        <span
                            class="truncate text-base font-medium"
                            :class="opened ? 'inline' : 'hidden xl:inline'"
                        >
                            {{ link.name }}
                        </span>

                        <div
                            v-if="link.count !== undefined || link.badge"
                            class="ml-auto hidden items-center gap-2"
                            :class="
                                opened ? 'inline-flex' : 'hidden xl:inline-flex'
                            "
                        >
                            <span
                                v-if="link.count !== undefined"
                                class="h-5 min-w-[37px] items-center justify-center rounded-2xl bg-gray-100 px-2 text-xs font-normal text-gray-500"
                            >
                                {{ link.count }}
                            </span>
                            <span
                                v-if="link.badge"
                                class="inline-flex h-5 items-center justify-center rounded-2xl bg-[#FF421E] px-2 text-[10px] font-semibold text-white"
                            >
                                {{ link.badge }}
                            </span>
                        </div>
                    </div>
                </inertia-link>
            </nav>

            <div class="mt-auto pb-2">
                <div
                    v-if="globalProps.isTrialing || globalProps.isTrialExpired"
                    class="w-full px-[10px]"
                    :class="opened ? 'block' : 'hidden xl:block'"
                >
                    <div
                        class="w-full rounded-[6.75px] bg-[#0B1220] p-4 text-gray-200 shadow-sm"
                    >
                        <div
                            class="flex items-center gap-2 text-[11px] font-medium"
                        >
                            <span
                                v-if="globalProps.isTrialing"
                                class="h-2 w-2 shrink-0 rounded-[6.75px] bg-emerald-400"
                            ></span>

                            <span
                                v-if="globalProps.isTrialing"
                                class="truncate"
                            >
                                {{ $t("vue.free_trial_process_bar") }}:
                                {{ daysLeft }} {{ $t("vue.days_left") }} ({{
                                    usedDays
                                }}/{{ totalDays }} {{ $t("vue.days_used") }})
                            </span>

                            <span v-else class="truncate">
                                {{ $t("vue.your_free_trial_expired") }}
                            </span>
                        </div>

                        <div
                            v-if="globalProps.isTrialing"
                            class="mt-2 h-1 w-full rounded-[6.75px] bg-gray-700/70"
                        >
                            <div
                                class="h-1 rounded-[6.75px]l bg-[#04C951]"
                                :style="{
                                    width:
                                        progress > 0 ? progress + '%' : '2px',
                                }"
                            ></div>
                        </div>

                        <div
                            v-else
                            class="mt-2 h-1 w-full rounded-[6.75px] bg-[#FF0000]"
                        ></div>

                        <button
                            type="button"
                            class="mt-3 w-full rounded-[6.75px] bg-[#088BEA] py-2 text-sm font-semibold text-white hover:bg-[#0A7ED3]"
                            @click="goToSubscriptions"
                        >
                            {{ $t("vue.subscribe_now") }}
                        </button>
                    </div>
                </div>

                <div class="mt-[4px] w-full px-[10px] pb-[7px]">
                    <LanguageSwitcher
                        :compact="!opened && !isXl"
                        :sidebar-right="!opened && !isXl ? 64 : 250"
                    />
                </div>

                <div class="mt-[4px] w-full px-[10px] pb-[7px]">
                    <a
                        v-tooltip="{
                            content: 'Support',
                            delay: { show: 1250, hide: 150 },
                        }"
                        href="https://docs.dmarceye.com"
                        target="_blank"
                        rel="noopener"
                        class="group block w-full"
                        @click="$emit('close')"
                    >
                        <div
                            class="w-full rounded-[6.75px] transition-all duration-200 hover:bg-gray-200"
                            data-helper-tooltip="support"
                        >
                            <div
                                class="flex h-12 w-full items-center px-4 py-3 text-base font-medium text-gray-500 transition-all duration-200 hover:text-gray-600"
                                :class="
                                    (!opened ? 'justify-center' : 'gap-7') +
                                    ' xl:justify-start xl:gap-7'
                                "
                            >
                                <BookOpenIcon
                                    class="h-5 w-5 shrink-0 transition duration-200 group-hover:text-gray-600"
                                />

                                <span
                                    class="truncate"
                                    :class="
                                        opened ? 'inline' : 'hidden xl:inline'
                                    "
                                >
                                    <p
                                        v-if="opened"
                                        class="mx-4 hidden h-5 whitespace-nowrap md:block xl:hidden"
                                    >
                                        {{ t("vue.documentation") }}
                                    </p>

                                    <p class="hidden xl:block">
                                        {{ t("vue.documentation") }}
                                    </p>
                                </span>
                            </div>
                        </div>
                    </a>
                </div>

                <!--TODO: delete sidepanel teams menu later fully as a whole implementation-->

                <div
                    class="w-16 flex-1 space-y-1 px-3 transition-[width] duration-200 w-full xl:px-4"
                >
                    <a
                        v-tooltip="{
                            content: 'Changelog',
                            delay: { show: 1250, hide: 150 },
                        }"
                        href="/changelog"
                        class="group flex items-center justify-between rounded-md px-2 py-2 text-sm font-medium transition-all xl:w-auto"
                        @click="$emit('close')"
                    >
                        <div class="w-full">
                            <div
                                v-if="!opened"
                                class="flex h-10 items-center justify-center xl:hidden"
                            >
                                <LightBulbIcon
                                    class="h-6 w-6 shrink-0 text-gray-500 transition duration-200 group-hover:text-gray-600"
                                />
                            </div>

                            <div
                                class="border-t border-gray-200 pt-4 space-y-1"
                                :class="opened ? 'block' : 'hidden xl:block'"
                            >
                                <p
                                    class="text-base font-semibold text-gray-600 whitespace-nowrap"
                                >
                                    DMARCeye {{ $t("vue.version") }} 1.4.4
                                </p>
                                <p
                                    class="text-sm text-gray-400 whitespace-nowrap"
                                >
                                    {{ $t("vue.last_update") }} 30/01/2026
                                </p>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {
    Bars3BottomLeftIcon,
    XMarkIcon,
    BookOpenIcon,
    LightBulbIcon,
} from "@heroicons/vue/24/outline";

import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations.ts";
import { router } from "@inertiajs/vue3";

const isXl = ref(false);

const updateIsXl = () => {
    isXl.value = window.matchMedia("(min-width: 1326px)").matches;
};

onMounted(() => {
    updateIsXl();
    window.addEventListener("resize", updateIsXl, { passive: true });
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", updateIsXl);
});

const goToSubscriptions = () => {
    router.visit(route("billing.subscriptions.index"));
};

defineProps({
    opened: {
        type: Boolean,
        default: false,
    },
});

defineEmits(["open", "close"]);

const { links } = sidebar();

const page = computed(() => usePage<IRhizomPageProps>().url);
const globalProps = computed(() => usePage<IRhizomPageProps>().props);

const { t } = useTranslations();

const totalDays = computed(() =>
    Number(globalProps.value.totalDays ?? globalProps.value.defaultTrialDays),
);
const usedDays = computed(() => Number(globalProps.value.usedDays ?? 0));

const daysLeft = computed(() => Math.max(0, totalDays.value - usedDays.value));

const progress = computed(() => {
    const t = totalDays.value || 1;
    const p = (usedDays.value / t) * 100;
    return Math.max(0, Math.min(100, p));
});
</script>

<style></style>
